// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { PaginationModel } from "@insite/client-framework/Types/ApiModels";
import { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import ManagedAccordionSection from "@steiner/MobiusOverrides/AccordionSection/ManagedAccordionSection";
import { xTranslate } from "@steiner/XDL/Utilities/translate";
import React, { FC, PropsWithChildren, useMemo, useState } from "react";

interface HeadingAccordionProps {
    isLoading?: boolean;
    title: string;
    start?: number;
    end?: number;
    total?: number;
    viewAll?: string;
    headingExtraChildren?: React.ReactElement;
    styles?: HeadingAccordionStyles;
    showViewAll?: boolean;
    showPagination?: boolean;
    initialExpanded?: boolean;
    extendedStyles?: HomeAccordionStyles;
}

interface ShowingTypographyProps extends Pick<HeadingAccordionProps, "start" | "end" | "total"> {
    styles?: HeadingAccordionStyles["showing"];
    displayAsAccordion?: boolean;
}

interface Props extends Omit<HeadingAccordionProps, "start" | "end" | "total"> {
    pagination?: PaginationModel | null;
    /**
     * Number of items for the current object.
     * For example, in pagination, this reflects the total number of items
     * for the current object (e.g., the collection or context),
     * as opposed to the number of items returned in a specific request.
     */
    itemsCount?: number;
    parsedClassName?: string;
}

interface HeadingAccordionStyles {
    container?: InjectableCss;
    innerContainer?: InjectableCss;
    title?: TypographyPresentationProps;
    showing?: TypographyPresentationProps;
    viewAll?: LinkPresentationProps;
}

export interface HomeAccordionStyles {
    accordionSection?: AccordionSectionPresentationProps;
    headingAccordion?: HeadingAccordionStyles;
}

const ShowingTypography: FC<ShowingTypographyProps> = ({
    total = 0,
    start = 0,
    end = 0,
    displayAsAccordion,
    styles,
}) => {
    return (
        <Typography {...styles} data-is-accordion={displayAsAccordion}>
            {xTranslate(
                "Showing {0} out of {1}",
                <Typography weight="bold">{`${start}-${end}`}</Typography>,
                <Typography weight="bold">{total}</Typography>,
            )}
        </Typography>
    );
};

const HeadingAccordion: FC<HeadingAccordionProps> = ({
    title,
    start,
    end,
    total,
    viewAll,
    headingExtraChildren = null,
    styles,
    showViewAll = true,
    showPagination = true,
}) => {
    return (
        <StyledWrapper {...styles?.container}>
            <Typography {...styles?.title}>{title}</Typography>
            <StyledWrapper {...styles?.innerContainer}>
                {showPagination && (
                    <ShowingTypography
                        start={start}
                        end={end}
                        total={total}
                        displayAsAccordion={true}
                        styles={styles?.showing}
                    />
                )}
                {headingExtraChildren}
                {showViewAll && (
                    <Link href={viewAll} {...styles?.viewAll}>
                        {translate("View All")}
                    </Link>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export const homeAccordionStyles: HomeAccordionStyles = {};

const HomeAccordion: FC<PropsWithChildren<Props>> = ({
    isLoading,
    itemsCount,
    title,
    pagination,
    viewAll,
    children,
    headingExtraChildren,
    showViewAll,
    showPagination,
    initialExpanded = true,
    parsedClassName,
    extendedStyles,
}) => {
    const [styles] = useState(() => mergeToNew(homeAccordionStyles, extendedStyles));
    const isTabletScreen = useAppSelector(state => (state as CustomAppState).xCommon.isTabletScreen);

    const { start, end, totalItemCount } = useMemo(() => {
        // Pagination.page shows 1 even when no results or pages are returned thats why we check for numberOfPages
        const start = pagination && pagination.numberOfPages > 0 ? (pagination.page - 1) * pagination.pageSize + 1 : 0;
        const end = pagination && pagination.numberOfPages > 0 ? start + (itemsCount || 1) - 1 : undefined;
        return { start, end, totalItemCount: pagination?.totalItemCount };
    }, [pagination, itemsCount]);

    return (
        <LoadingOverlay loading={isLoading} className={parsedClassName}>
            <ManagedAccordionSection
                initialExpanded={initialExpanded}
                stopRowClickable={!isTabletScreen}
                title={
                    <HeadingAccordion
                        title={title}
                        start={start}
                        end={end}
                        total={totalItemCount}
                        viewAll={viewAll}
                        headingExtraChildren={headingExtraChildren}
                        styles={styles.headingAccordion}
                        showViewAll={showViewAll}
                        showPagination={showPagination}
                    />
                }
                {...styles.accordionSection}
            >
                {showPagination && (
                    <ShowingTypography
                        start={start}
                        end={end}
                        total={totalItemCount}
                        displayAsAccordion={false}
                        styles={styles.headingAccordion?.showing}
                    />
                )}
                {children}
            </ManagedAccordionSection>
        </LoadingOverlay>
    );
};

export default HomeAccordion;
